import React, { useState, useEffect, useCallback } from 'react';
import Image from 'next/image';
import { useSelector } from 'react-redux';
import { Quote, ChevronLeft, ChevronRight } from 'lucide-react';

import SectionHeading from 'components/SectionHeading';

export default function TestimonialsSection({ testimonials = [] }) {
    const lang = useSelector((s) => s.system.lang || 'mn');
    const [active, setActive] = useState(0);

    const count = testimonials?.length || 0;

    useEffect(() => {
        if (count <= 1) return;
        const id = setInterval(() => {
            setActive((p) => (p + 1) % count);
        }, 6000);
        return () => clearInterval(id);
    }, [count]);

    const goNext = useCallback(() => {
        if (count === 0) return;
        setActive((p) => (p + 1) % count);
    }, [count]);

    const goPrev = useCallback(() => {
        if (count === 0) return;
        setActive((p) => (p - 1 + count) % count);
    }, [count]);

    if (!testimonials || count === 0) return null;

    const current = testimonials[active];
    const name = lang === 'en' && current.nameEn ? current.nameEn : current.name;
    const position = lang === 'en' && current.positionEn ? current.positionEn : current.position;
    const quote = lang === 'en' && current.quoteEn ? current.quoteEn : current.quote;
    const imageUrl = current.imageUrl || '/placeholders/avatar-default.svg';

    return (
        <section className="py-20 lg:py-28 bg-brand-cream">
            <div className="max-w-[1440px] mx-auto px-4 lg:px-8">
                <SectionHeading
                    eyebrow={lang === 'en' ? 'Testimonials' : 'Удирдлагын мэндчилгээ'}
                    title={lang === 'en' ? 'What Leaders Say' : 'Удирдлагуудын үг'}
                />

                <div className="max-w-5xl mx-auto">
                    <div className="grid grid-cols-1 lg:grid-cols-[280px_1fr] gap-10 lg:gap-14 items-center">
                        {/* Image */}
                        <div className="flex justify-center lg:justify-start">
                            <div className="relative">
                                <div className="absolute -inset-3 border-2 border-brand-yellow" />
                                <div className="relative w-[240px] h-[300px] lg:w-[260px] lg:h-[340px] overflow-hidden bg-white">
                                    <Image
                                        key={current.id}
                                        src={imageUrl}
                                        alt={name}
                                        fill
                                        sizes="260px"
                                        className="object-cover animate-fade-in"
                                    />
                                </div>
                            </div>
                        </div>

                        {/* Quote */}
                        <div key={current.id} className="animate-fade-in">
                            <Quote size={48} className="text-brand-yellow mb-4" strokeWidth={1} />
                            <blockquote className="text-lg lg:text-2xl leading-relaxed text-brand-dark mb-8 font-medium">
                                {quote}
                            </blockquote>
                            <div className="flex items-center gap-4 mb-8">
                                <div className="w-12 h-px bg-brand-yellow" />
                                <div>
                                    <p className="font-heading uppercase font-bold text-xl text-brand-dark">
                                        {name}
                                    </p>
                                    {position && (
                                        <p className="text-sm text-brand-yellow-dark mt-1">
                                            {position}
                                        </p>
                                    )}
                                </div>
                            </div>

                            {/* Controls */}
                            <div className="flex items-center justify-between">
                                <div className="flex gap-2">
                                    <button
                                        onClick={goPrev}
                                        aria-label="Previous"
                                        className="w-11 h-11 flex items-center justify-center border-2 border-brand-dark text-brand-dark hover:bg-brand-yellow hover:border-brand-yellow transition"
                                    >
                                        <ChevronLeft size={18} />
                                    </button>
                                    <button
                                        onClick={goNext}
                                        aria-label="Next"
                                        className="w-11 h-11 flex items-center justify-center border-2 border-brand-dark text-brand-dark hover:bg-brand-yellow hover:border-brand-yellow transition"
                                    >
                                        <ChevronRight size={18} />
                                    </button>
                                </div>
                                <div className="font-heading text-sm text-brand-gray">
                                    <span className="text-brand-yellow-dark font-bold">
                                        {String(active + 1).padStart(2, '0')}
                                    </span>
                                    <span className="mx-1">/</span>
                                    {String(count).padStart(2, '0')}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
}